import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from './Infotooltip';

function ChartTooltipContent({ active, payload, label, t }) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">
              {payload[0].payload.period === 'day' ? t('time') : t('date')}
            </span>
            <span className="font-bold text-muted-foreground">{label}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">Rate</span>
            <span className="font-bold">{payload[0].value.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

// Static wavy path shaped like the real trend line — flat baseline, a
// small bump, a dip, a taller bump, flat, a couple of small spikes, flat.
// Used only as a loading placeholder, never real data.
const SKELETON_PATH_FILL =
  "M0,36 L8,36 C10,20 13,20 15,36 L18,36 C20,26 23,26 25,36 " +
  "L30,36 C33,14 38,14 41,36 L46,36 C48,24 51,24 53,36 " +
  "L62,36 C64,30 66,30 68,36 L70,36 C72,18 74,18 76,36 " +
  "L100,36 L100,40 L0,40 Z";
const SKELETON_PATH_STROKE =
  "M0,36 L8,36 C10,20 13,20 15,36 L18,36 C20,26 23,26 25,36 " +
  "L30,36 C33,14 38,14 41,36 L46,36 C48,24 51,24 53,36 " +
  "L62,36 C64,30 66,30 68,36 L70,36 C72,18 74,18 76,36 L100,36";

export function RecyclingChart({ data, loading = false }) {
  const { t, i18n } = useTranslation();

  const chartData = data?.historical_data?.map(item => ({
    date: item.date,
    recyclingRate: item.rate,
    period: data.period
  })) || [];

  const currentRate = data?.recycling_rate || 0;
  const percentage  = currentRate.toFixed(1);
  const period      = data?.period || 'year';

  const getColorScheme = (rate) => {
    if (rate >= 70) return { stroke: '#10b981', fill: '#10b981', fillOpacity: 0.2, text: 'text-green-600' };
    if (rate >= 50) return { stroke: '#3b82f6', fill: '#3b82f6', fillOpacity: 0.2, text: 'text-blue-500' };
    return               { stroke: '#ef4444', fill: '#ef4444', fillOpacity: 0.2, text: 'text-red-500' };
  };

  const colorScheme = getColorScheme(currentRate);

  const getRangeLabel = () => {
    if (!data?.start_date || !data?.end_date) return null;
    // FIX: toLocaleDateString with no timeZone renders in the *browser's*
    // local timezone, not the server's. The backend already computes the
    // right date boundary in Europe/Berlin time — letting the browser
    // reinterpret that instant in its own timezone can roll it back a day
    // (e.g. dev machine set to a timezone with a smaller UTC offset than
    // Berlin's CEST). Pin timeZone explicitly everywhere a Date derived
    // from an API iso string gets displayed.
    const fmt = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Berlin' });
    const isCustom = period?.startsWith('custom');
    if (isCustom)        return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
    if (period === 'day')   return fmt(data.start_date);
    if (period === 'month') return new Date(data.start_date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });
    // FIX: .getFullYear() always reads the LOCAL year with no timezone
    // override available — swapped for toLocaleDateString with timeZone set,
    // same as the other branches.
    if (period === 'year')  return new Date(data.start_date).toLocaleDateString(i18n.language, { year: 'numeric', timeZone: 'Europe/Berlin' });
    return `${fmt(data.start_date)} – ${fmt(data.end_date)}`;
  };

  if (loading) {
    // Skeleton — header stays real, percentage becomes a bar, and the
    // chart area gets a wavy line shaped like the real trend (flat with
    // a couple of bumps) so it reads as "a chart is coming", not a blank box.
    return (
      <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 flex flex-col w-full h-[200px] motion-safe:animate-pulse" aria-busy="true">
        <span className="sr-only" role="status">{t('recyclingRate.loading', 'Loading…')}</span>
        <div className="px-4 py-3 flex items-center justify-between">
          <h2 className="tracking-tight text-sm font-medium pb-2">{t('recyclingRate.defaultTitle')}</h2>
          <div className="flex items-center gap-2 pb-2">
            <InfoTooltip text="Percentage of your waste that was correctly sorted into a recycling stream (PMD, organic, or paper), excluding items that were contaminated." />
          </div>
        </div>

        <div className="px-4 pb-1">
          <div className="h-6 w-16 rounded bg-gray-200 dark:bg-slate-700" />
        </div>

        <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full" aria-hidden="true">
            <path d={SKELETON_PATH_FILL} className="fill-gray-200 dark:fill-slate-700" />
            <path d={SKELETON_PATH_STROKE} fill="none" className="stroke-gray-300 dark:stroke-slate-600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium pb-2">{t('recyclingRate.defaultTitle')}</h2>
        <div className="flex items-center gap-2 pb-2">
          {period?.startsWith('custom') && getRangeLabel() && (
            <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
          )}
          <InfoTooltip text="Percentage of your waste that was correctly sorted into a recycling stream (PMD, organic, or paper), excluding items that were contaminated." />
        </div>
      </div>

      <div className={`px-4 pb-1 text-2xl font-bold ${colorScheme.text}`}>
        {percentage}%
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              dy={10}
              interval="preserveStartEnd"
              tickFormatter={(value) => {
                // FIX: backend sends pre-formatted "HH:00" strings for the
                // day view (e.g. "14:00") — that's not a parseable Date on
                // its own, so new Date(value) produced "Invalid Date".
                // It's already display-ready, just pass it through.
                if (period === 'day') return value;
                if (period === 'week') return value;
                // Custom periods return pre-formatted strings ("07 Feb", "Feb 2026") — pass through as-is
                if (period?.startsWith('custom')) return value;
                try {
                  const date = new Date(value);
                  if (isNaN(date.getTime())) return value;
                  return date.toLocaleDateString(i18n.language, { month: 'short' });
                } catch (e) { return value; }
              }}
            />
            <YAxis hide domain={[0, 100]} />
            <Tooltip content={<ChartTooltipContent t={t} />} wrapperStyle={{ outline: 'none' }} />
            <Area
              type="monotone"
              dataKey="recyclingRate"
              stroke={colorScheme.stroke}
              strokeWidth={3}
              fill={colorScheme.fill}
              fillOpacity={colorScheme.fillOpacity}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}